import { get } from 'lodash'

import { Colors, Services } from '@sigma-frontend-libs/commons'
import { SENDING_STATUS, WEBHOOK_EVENTS } from '@sigma-legacy-libs/essentials/lib/constants'

import { buttonModes, components, icons, labelModes, sizes } from '@/utils'

import button from '@/components/button'
import squircle from '@/components/icon/squircle'
import arrow from '@/components/misc/arrow'
import { sendingsStatuses } from '@/components/services/sendings/utils'
import tag from '@/components/tag'

const defaultLayout = {
  class: 'grid grid-gap--8 faifs fjife',
  style: { 'grid-template-columns': '120px 1fr' }
}
const defaultProps = (options, type, field) => {
  const { defaults } = options

  return {
    defaultValue: get(defaults, [ options.path, type, field ].join('.')),
    labelMode: labelModes.hidden,
    readonly: options.permission === 'read',
    dense: true,
    clearable: true,
    details: false
  }
}

function renderMethodsSelector(h, type, options) {
  return h(
    components.select,
    {
      props: {
        value: this.proxy[type].method,
        ...defaultProps(options, type, 'method'),
        items: [ 'GET', 'POST' ]
      },
      on: {
        input: event => {
          this.proxy[type].method = event
        }
      }
    }
  )
}
function renderLabel(h, label, color = Colors.grey) {
  return h(
    tag,
    {
      props: {
        label,
        color,
        size: sizes.small
      }
    }
  )
}
function renderGeneral(h, type, options) {
  const { path, serviceName } = options

  return h(
    components.card,
    {
      class: 'grid grid-gap--8 pa-2',
      props: {
        color: Colors.border,
        flat: true
      }
    },
    [
      h(
        'div',
        defaultLayout,
        [
          renderLabel.call(this, h, this.getTranslate(`${serviceName}.labels.${path}.method`)),
          renderMethodsSelector.call(this, h, type, options)
        ]
      ),

      h(
        'div',
        defaultLayout,
        [
          renderLabel.call(this, h, this.getTranslate(`${serviceName}.labels.${path}.url`)),
          h(
            components['text-field'],
            {
              props: {
                value: this.proxy[type].url,
                ...defaultProps(options, type, 'url')
              },
              on: {
                input: event => {
                  this.proxy[type].url = event
                }
              }
            }
          )
        ]
      ),

      h(
        'div',
        defaultLayout,
        [
          renderLabel.call(this, h, this.getTranslate(`${serviceName}.labels.${path}.headers`)),
          h(
            components.textarea,
            {
              props: {
                value: this.proxy[type].headers,
                ...defaultProps(options, type, 'headers'),
                grow: true
              },
              on: {
                input: event => {
                  this.proxy[type].headers = event
                }
              }
            }
          )
        ]
      ),

      h(
        'div',
        defaultLayout,
        [
          renderLabel.call(this, h, this.getTranslate(`${serviceName}.labels.${path}.body`)),
          h(
            components.textarea,
            {
              props: {
                value: this.proxy[type].body,
                ...defaultProps(options, type, 'body'),
                grow: true
              },
              on: {
                input: event => {
                  this.proxy[type].body = event
                }
              }
            }
          )
        ]
      ),

      h(
        'div',
        defaultLayout,
        [
          renderLabel.call(this, h, this.getTranslate(`${serviceName}.labels.${path}.maxConnections`)),
          h(
            components['text-field'],
            {
              props: {
                value: this.proxy[type].maxConnections,
                hint: this.getTranslate(`${serviceName}.hints.${path}.maxConnections`),
                ...defaultProps(options, type, 'maxConnections'),
                type: 'number',
                step: 'any',
                details: true
              },
              on: {
                input: event => {
                  this.proxy[type].maxConnections = event
                }
              }
            }
          )
        ]
      )
    ]
  )
}
function renderSuccessOn(h, type, options) {
  const { path, serviceName } = options

  return h(
    components.card,
    {
      class: 'grid grid-gap--8 pa-2',
      props: {
        color: Colors.success,
        flat: true
      }
    },
    [
      h(
        'div',
        defaultLayout,
        [
          renderLabel.call(this, h, this.getTranslate(`${serviceName}.labels.${path}.successOn.status`)),
          h(
            components['text-field'],
            {
              props: {
                value: this.proxy[type].successOn.status,
                hint: this.getTranslate(`${serviceName}.hints.${path}.successOn.status`),
                ...defaultProps(options, type, 'successOn.status'),
                details: true
              },
              on: {
                input: event => {
                  this.proxy[type].successOn.status = event
                }
              }
            }
          )
        ]
      ),

      h(
        'div',
        defaultLayout,
        [
          renderLabel.call(this, h, this.getTranslate(`${serviceName}.labels.${path}.successOn.body`)),
          h(
            components['text-field'],
            {
              props: {
                value: this.proxy[type].successOn.body,
                hint: this.getTranslate(`${serviceName}.hints.${path}.successOn.body`),
                ...defaultProps(options, type, 'successOn.body'),
                details: true
              },
              on: {
                input: event => {
                  this.proxy[type].successOn.body = event
                }
              }
            }
          )
        ]
      )
    ]
  )
}
function renderStatus(h, type, options, status) {
  return h(
    'div',
    defaultLayout,
    [
      h(
        tag,
        {
          props: {
            label: this.getTranslate(`${Services.sendings}.statuses.${status}`),
            color: sendingsStatuses[status].color,
            size: sizes.small
          }
        }
      ),
      h(
        components['text-field'],
        {
          props: {
            value: this.proxy[type].extras.statuses[status],
            ...defaultProps(options, type, `extras.statuses.${status}`)
          },
          on: {
            input: event => {
              this.proxy[type].extras.statuses[status] = event
            }
          }
        }
      )
    ]
  )
}
function renderStatuses(h, type, options) {
  return h(
    components.card,
    {
      class: 'grid grid-gap--8 pa-2',
      props: {
        color: Colors.border,
        flat: true
      }
    },
    [
      SENDING_STATUS.map(status => {
        return renderStatus.call(this, h, type, options, status)
      })
    ]
  )
}
function renderFormItemHeader(h, type, options, group, expanded) {
  const { path, serviceName } = options

  return h(
    'div',
    {
      class: 'default-expansion-panel-header'
    },
    [
      this.getTranslate(`${serviceName}.subtitles.${path}.${group}`),
      h(arrow, { props: { expanded } })
    ]
  )
}
function renderFormItem(h, type, options, group) {
  if (group === 'statuses' && type !== WEBHOOK_EVENTS.onStatusChange) {
    return
  }

  return h(
    components['expansion-panel'],
    {
      scopedSlots: {
        header: ({ expanded }) => {
          return renderFormItemHeader.call(this, h, type, options, group, expanded)
        },
        default: () => {
          switch (group) {
            case 'request': return renderGeneral.call(this, h, type, options)
            case 'successOn': return renderSuccessOn.call(this, h, type, options)
            case 'statuses': return renderStatuses.call(this, h, type, options)
          }
        }
      }
    }
  )
}
function renderForm(h, type, options) {
  return h(
    components['expansion-panels'],
    {
      props: {
        flat: true
      }
    },
    [
      renderFormItem.call(this, h, type, options, 'request'),
      renderFormItem.call(this, h, type, options, 'successOn'),
      renderFormItem.call(this, h, type, options, 'statuses')
    ]
  )
}

function renderItemHeader(h, event, options, expanded) {
  const { path, serviceName } = options

  return h(
    'div',
    {
      class: 'default-expansion-panel-header pl-2'
    },
    [
      h(
        'div',
        {
          class: 'row faic'
        },
        [
          h(
            squircle,
            {
              props: {
                icon: icons.webhook,
                color: Colors.grey
              }
            }
          ),

          this.getTranslate(`${serviceName}.subtitles.${path}.${event}`)
        ]
      ),

      h(arrow, { props: { expanded } })
    ]
  )
}
function renderItemBody(h, event, options) {
  return renderForm.call(this, h, event, options)
}
function renderItem(h, event, options) {
  return h(
    components['expansion-panel'],
    {
      scopedSlots: {
        header: ({ expanded }) => {
          return renderItemHeader.call(this, h, event, options, expanded)
        },
        default: () => {
          return renderItemBody.call(this, h, event, options)
        }
      }
    }
  )
}
function renderItems(h, options) {
  return WEBHOOK_EVENTS.map(event => {
    return renderItem.call(this, h, event, options)
  })
}

function renderBody(h, options) {
  return h(
    components['expansion-panels'],
    {
      props: {
        outline: true,
        rounded: true
      }
    },
    [ renderItems.call(this, h, options) ]
  )
}

function renderFooter(h, options) {
  if (options.permission === 'write') {
    return h(
      'div',
      {
        class: 'fjcfe'
      },
      [
        h(
          button,
          {
            props: {
              label: this.getTranslate('misc.buttons.save'),
              mode: buttonModes.flat,
              color: Colors.primary,
              disabled: this.$_disabled
            },
            on: {
              click: () => {
                this.transmitValue()
              }
            }
          }
        )
      ]
    )
  }
}

export default function(h, options) {
  if (options.permission !== false) {
    return h(
      'div',
      {
        class: 'grid grid-gap--8'
      },
      [
        renderBody.call(this, h, options),
        renderFooter.call(this, h, options)
      ]
    )
  }
}
